import { createContext, useContext } from 'react';
import { useImmerReducer } from 'use-immer';
import { enableMapSet } from 'immer';
import { makeProvider } from '../../helpers/makeContexts';

enableMapSet();

/*
@filters: all filters on this dash
{
    1: {
      "id": 1,
      "filterConfig": { "column": "Student_Id", "value": "2748141" },
      "widgetIds": [1, 2, 3]
    },
    2: {
      "id": 2,
      "filterConfig": { "column": "Student_Id", "value": "2748141" },
      "widgetIds": [1, 2, 3]
    },
  ...
}
@activeFilters: filters that active on widgets
{
    1: {
      "id": 1,
      "filterConfig": { "column": "Student_Id", "value": "2748141" },
      "widgetIds": [1, 2, 3]
    },
    2: {
      "id": 2,
      "filterConfig": { "column": "Student_Id", "value": "2748141" },
      "widgetIds": [1, 2, 3]
    },
  ...
}
 @crossFilter: filter that used for crossing filtering when click on Chart
{
    colName: [value]
}
@stageFilters: Holds filter changes before they are applied
{
    1: {
      "id": 1,
      "filterConfig": { "column": "Student_Id", "value": "2748141" },
      "widgetIds": [1, 2, 3]
    },
    2: {
      "id": 2,
      "filterConfig": { "column": "Student_Id", "value": "2748141" },
      "widgetIds": [1, 2, 3]
    },
  ...
}  */

const dashboardViewsFiltersInit = {
  views: [],
  filters: {},
  activeFilters: {},
  crossFilter: {},
  stageFilters: {}, // Holds filter changes before they are applied
  studentIdCache: {}, // Holds the heavy studentId sets for each filter
  stageView: {}, // Holds view changes before they are applied
  activeViewId: null,
  viewSaveEnabled: { enabled: false, buttonText: 'Save' },
  idsForInvalidFilters: [],
  maxFilterLimit: 15,
  selectedWidgetIds: new Set(), //widget Id set for building group
  selectedGroupIds: new Set(), //selected group Ids for ungroup
  selectedGroupIdToAddFilters: null, //selected group id to adding filter to this group
  openGroupController: false, //boolean to open the right group panel
  pendingGroupAction: null, // Store info for deferred drag-drop actions
  saveViewFromGroup: false, // a boolean to determine where save view is triggered
  isLoadingViewAndFilters: false,
};

function dashboardViewsFiltersReducer(draft, { type, payload }) {
  switch (type) {
    case 'setViews': {
      draft.views = payload;
      break;
    }
    case 'setActiveFilters': {
      //first check if crossFilter contains anything, if yes, set it as {}
      if (Object.keys(draft.crossFilter).length > 0) {
        draft.crossFilter = {};
      }
      draft.activeFilters = payload;
      break;
    }
    case 'setCrossFilter': {
      const key = Object.keys(payload)[0];

      if (draft.crossFilter[key]) {
        //delete it
        delete draft.crossFilter[key];
      } else {
        draft.crossFilter = payload;
      }

      break;
    }
    case 'setStageFilters': {
      draft.stageFilters = payload;
      break;
    }
    case 'setStudentIdCache': {
      draft.studentIdCache = payload;
      break;
    }
    case 'removeStudentIdCache': {
      for (const id of payload) {
        delete draft.studentIdCache[id];
      }
      break;
    }
    case 'setStageView': {
      draft.stageView = payload;
      break;
    }
    case 'updateFilterOrderInView': {
      draft.stageView.filterOrder = payload;
      break;
    }
    case 'addFilterToStageFilters': {
      draft.stageFilters[payload.filterId] = payload.filterData;
      break;
    }
    case 'setFilters': {
      draft.filters = payload;
      break;
    }
    case 'setIdsForInvalidFilters': {
      draft.idsForInvalidFilters = payload;
      break;
    }
    case 'setActiveViewId': {
      draft.activeViewId = payload;
      break;
    }
    case 'setViewSaveEnabled': {
      draft.viewSaveEnabled = payload;
      break;
    }
    case 'setSelectedWidgetIds': {
      //if the id in the payload is in the set, remove it, otherwise add it
      if (draft.selectedWidgetIds.has(payload)) {
        draft.selectedWidgetIds.delete(payload);
      } else {
        draft.selectedWidgetIds.add(payload);
      }
      break;
    }
    case 'setSelectedGroupIds': {
      if (draft.selectedGroupIds.has(payload)) {
        draft.selectedGroupIds.delete(payload);
      } else {
        draft.selectedGroupIds.add(payload);
      }
      break;
    }
    case 'setSingleSelectedGroupIds': {
      draft.selectedGroupIds = new Set([payload]);
      break;
    }
    case 'setOpenGroupController': {
      draft.openGroupController = payload;
      break;
    }
    case 'clearSelectedWidgetIds': {
      draft.selectedWidgetIds = new Set();
      break;
    }
    case 'clearSelectedGroupIds': {
      draft.selectedGroupIds = new Set();
      break;
    }
    case 'setSelectedGroupIdToAddFilters': {
      draft.selectedGroupIdToAddFilters = payload;
      break;
    }
    case 'setPendingGroupAction': {
      draft.pendingGroupAction = payload;
      break;
    }
    case 'setSaveViewFromGroup': {
      draft.saveViewFromGroup = payload;
      break;
    }
    case 'clearAll': {
      draft.activeFilters = {};
      draft.stageFilters = {};
      draft.activeViewId = null;
      draft.stageView = {};
      draft.viewSaveEnabled = { enabled: true, buttonText: 'Save' };
      draft.selectedGroupIdToAddFilters = null;
      break;
    }
    case 'setIsLoadingViewAndFilters': {
      draft.isLoadingViewAndFilters = payload;
      break;
    }
    default: {
      return null;
    }
  }
}

const [DashboardViewsFiltersContext, DashboardViewsFiltersDispatch] = [
  createContext(null),
  createContext(null),
];

export const DashboardViewsFiltersProvider = makeProvider(
  DashboardViewsFiltersContext,
  DashboardViewsFiltersDispatch,
  dashboardViewsFiltersReducer,
  dashboardViewsFiltersInit,
);

export function useDashboardViewsFiltersStore() {
  return useContext(DashboardViewsFiltersContext);
}

export function useDashboardViewsFiltersDispatch() {
  return useContext(DashboardViewsFiltersDispatch);
}
